
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { DollarSign, Eye, Mail, Phone, User } from "lucide-react";
import { Cliente, Gerador } from "@/types";
import { useAuth } from "@/contexts/AuthContext";

interface ClientesListProps {
  clientes: Cliente[];
  geradores: Record<string, Gerador>;
  userCanAccessCliente: (cliente: Cliente) => boolean;
  userCanAccessGerador: (geradorId: string) => boolean;
}

const ClientesList = ({ clientes, geradores, userCanAccessCliente, userCanAccessGerador }: ClientesListProps) => {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[250px]">Nome</TableHead>
            <TableHead>Contato</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Localização</TableHead>
            <TableHead>Gerador</TableHead>
            <TableHead className="text-right">Consumo</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clientes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center h-24 text-muted-foreground">
                Nenhum cliente encontrado
              </TableCell>
            </TableRow>
          ) : (
            clientes.map((cliente) => (
              <TableRow key={cliente.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2 text-energy-blue" />
                    {cliente.nome}
                  </div>
                  <p className="text-xs text-muted-foreground truncate max-w-[220px]">
                    {cliente.documento}
                  </p>
                </TableCell>
                <TableCell>
                  {cliente.email && (
                    <div className="flex items-center text-sm">
                      <Mail className="h-3 w-3 mr-1 text-muted-foreground" />
                      <span className="truncate max-w-[150px]">{cliente.email}</span>
                    </div>
                  )}
                  {cliente.celular && (
                    <div className="flex items-center text-sm mt-1">
                      <Phone className="h-3 w-3 mr-1 text-muted-foreground" />
                      {cliente.celular}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    cliente.status === 'Ativo' ? 'bg-green-100 text-green-800' :
                    cliente.status === 'Inativo' ? 'bg-red-100 text-red-800' :
                    cliente.status === 'Pendente' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {cliente.status || 'Ativo'}
                  </span>
                </TableCell>
                <TableCell>
                  {cliente.cidade}/{cliente.estado}
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <Link 
                      to={`/geradores/${cliente.geradorId}`} 
                      className={`hover:underline mr-2 ${
                        cliente.geradorId && userCanAccessGerador(cliente.geradorId) 
                          ? "text-energy-green" 
                          : "text-gray-400 pointer-events-none"
                      }`}
                      onClick={(e) => {
                        if (!cliente.geradorId || !userCanAccessGerador(cliente.geradorId)) {
                          e.preventDefault();
                        }
                      }}
                    >
                      {geradores[cliente.geradorId]?.nomeTitular || "Não vinculado"}
                    </Link>
                    {cliente.geradorId && (
                      <Link to={`/financeiro/gerador/${cliente.geradorId}`}>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          disabled={!userCanAccessGerador(cliente.geradorId)}
                        >
                          <DollarSign className={`h-3 w-3 ${
                            userCanAccessGerador(cliente.geradorId) 
                              ? "text-energy-blue" 
                              : "text-gray-300"
                          }`} />
                        </Button>
                      </Link>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {cliente.consumoMedio?.toFixed(2) || "N/A"}
                  {cliente.consumoMedio ? " kWh" : ""}
                </TableCell>
                <TableCell className="text-right">
                  <Link to={`/clientes/${cliente.id}`}>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      disabled={!userCanAccessCliente(cliente)}
                      title={!userCanAccessCliente(cliente) ? "Acesso restrito" : "Ver detalhes"}
                    >
                      <Eye className={`h-4 w-4 ${!userCanAccessCliente(cliente) ? "text-gray-300" : ""}`} />
                    </Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ClientesList;
